/**
 * Unterseite „Wie der Anfo-Wert entsteht" (/auswertungen/umsatz/anfo-wert) unter Modell m5 (seit 13.10.2026), Umbau
 * nach P2-V3 § 2.12: Reihenfolge W1 (je Produkt) → W3 (Bestellungen mit Hochrechnung) → W2 (je Angebot, als Gruppen
 * Produkt × Zahlungsart mit den Angeboten offen darunter). Alle sichtbaren Euro-Spalten NETTO, umgerechnet hier mit dem
 * `netto_faktor` der API; brutto nur im ⓘ. Marke „geschätzt" an Sofortzahlungs-Angeboten über dem Preisdeckel des
 * Modells (`wert_geschaetzt`, Regel aus `anfoWertModelM5.wertUeberPreisdeckel`). Quelle: `anfoWertDiagnoseService.ts`.
 * Rückweg m4: nur die Produkttabelle mit Hinweis. Workshop-Lücken 29.10.2026 (Paket M): davor die Bindungskurve
 * (`BindungKarte`, A1), am Ende der Prüfstand-Hinweis (`PruefstandKarte`, A3) — beide mit eigenem Endpunkt.
 */
import { FC, ReactNode, useMemo } from 'react';
import { Alert, Box, Tooltip } from '@mui/material';
import ChapterPage from '../../../components/auswertungen/ChapterPage';
import ChartCard from '../../../components/auswertungen/ChartCard';
import InfoTip from '../../../components/InfoTip';
import SortableTable, { Spalte, spaltenMeta } from '../../../components/auswertungen/SortableTable';
import SpaltenMenue from '../../../components/auswertungen/SpaltenMenue';
import { Breite, breiteAus, useSpaltenwahl } from '../../../components/auswertungen/spaltenwahl';
import Explainer, { ExplainerSection } from '../../../components/Explainer';
import { Etikett, Hinweis, int, Laden, nachGruppenReihenfolge, ProduktZelle, tEuro, useBreitePx, useLaden } from '../../../components/auswertungen/kennzahlTeile';
import { formatEur, formatPct, TERMS } from '../../../helpers/kpiDefinitions';
import { AnfoWertAngebot, AnfoWertBestellungen, AnfoWertProdukt, AnfoWertZelle, fetchAnfoWertModell, formatBerlinWallclock } from '../../../helpers/auswertungenData';
import { formatNumber } from '../../../helpers/numberUtils';
import BindungKarte from './BindungKarte';
import PruefstandKarte from './PruefstandKarte';

const ZAHLUNGSART: Record<'T' | 'S', string> = { T: 'Testphase', S: 'Sofortzahlung' };

/** Woher der Startwert einer Zahlungsart kommt, in Worten. */
const START_QUELLE: Record<string, string> = {
  poolMean: 'Schnitt aller Produkte',
  countedPrices: 'aus ihren Preisen',
  knownPrices: 'aus Preisen jüngerer Anfos',
  knownPricesOld: 'aus Preisen älterer Anfos',
  offerConfig: 'aus dem Angebotspreis',
  poolCellMean: 'Schnitt aller Produkte',
};

/** Läufe in Worten (die Sicherung vor dem Umstieg ist kein Wertwechsel). */
const LAUF: Record<string, string> = {
  monthly: 'Monatslauf',
  cutover: 'Umstieg auf Modell m4',
  'cutover-m5': 'Umstieg auf Bewertung je Bestellung',
  rerun: 'Neuberechnung',
  'restore-m4': 'Rückweg auf Wert je Produkt',
  'restore-legacy': 'Altwerte zurückgeholt',
  'product-insert': 'neues Produkt angelegt',
  'offer-insert': 'Angebote neu bewertet',
};

/** E8/W2 (P2-V3 § 2.12): Wert unter der ersten Zahlung — derselbe Auswahleffekt wie „bezahlt > Wert" bei Zahlern. */
const UNTER_ERSTBETRAG =
  'Der Erwartete Wert mittelt über alle Besteller dieses Angebots, auch über die, die nie zahlen. Deshalb kann er unter der ersten Zahlung liegen.';

const EXPLAINER: ExplainerSection[] = [
  {
    label: 'Berechnung',
    body: 'Seit 13.10.2026 trägt jede Anfo ihren eigenen Wert. Ist ihre Zahlung alt genug (Sofortzahlung ab Tag 35, Testphase ab rund Tag 75), zählt, was sie bis Tag 75 gezahlt hat, mal Faktor auf 36 Monate für die gezahlte Art (Quartal oder Jahr) — mindestens aber das, was sie bis heute gezahlt hat. Eine Testphasen-Rechnung bis 1 € zählt nicht als Zahlung. Jüngere Anfos tragen den Wert ihres Angebots, ohne Angebotswert den Einzelwert ihres Produkts. Der Wert eines Angebots kommt aus Produkt und Zahlungsart: Schnitt der Zahlungen bis Tag 75 der gezählten Anfos, gemischt mit einem Startwert, mal Faktor auf 36 Monate. Der Einzelwert je Produkt mischt Testphase und Sofortzahlung nach ihrem Anteil; bei wenigen Anfos wird dieser Anteil zum Schnitt aller Produkte gezogen. Einmalkäufe: Preis × Anteil, der bezahlt wird, × 0,945. Einmal im Monat neu gerechnet und mit den zwei Vormonaten gemittelt; neue oder geänderte Angebote bewertet ein täglicher Lauf mit den Faktoren des Monatslaufs. Die Auswertungen der Sektion vergleichen mit dem Erwarteten Wert (Angebot, sonst Produkt); der Wert je Bestellung steht dort nur als „Hochrechnung aus den bisherigen Zahlungen“.',
  },
  {
    label: 'Brutto und netto',
    body: 'Das Modell rechnet mit den Zahlungen inkl. MwSt. Diese Seite zeigt alle Beträge netto: geteilt durch das gemessene Verhältnis brutto zu netto der bezahlten Anfos des Produkts (im Schnitt rund 6 %; Kunden aus dem Ausland zahlen keine MwSt.), Bestellwerte durch das Verhältnis der eigenen Zahlung. Die Bruttowerte stehen im ⓘ der Zelle.',
  },
  {
    label: 'Wichtig',
    highlight: true,
    body: 'Diese Seite erklärt den Wert, sie ändert ihn nicht. Läufe und den Einkaufspreis-Anteil stellt nur der Administrator ein.',
  },
];

const netto = (brutto: number | null | undefined, f: number): number | null => (brutto == null ? null : brutto / (f || 1));
const faktor = (v: number | null | undefined): string => (v === null || v === undefined ? '–' : formatNumber(v, 2));

/** Euro netto, brutto im ⓘ. */
const NettoEur: FC<{ brutto: number | null | undefined; f: number; zusatz?: string; dick?: boolean }> = ({ brutto, f, zusatz, dick }) =>
  brutto == null ? (
    <>–</>
  ) : (
    <Tooltip title={`inkl. MwSt. ${formatEur(brutto)}${zusatz ? ` · ${zusatz}` : ''}`} arrow placement="top">
      <Box component="span" sx={{ cursor: 'help', fontWeight: dick ? 600 : 400 }}>
        {formatEur(netto(brutto, f))}
      </Box>
    </Tooltip>
  );

// Unterzeilen dürfen umbrechen, auch in rechtsbündigen (nowrap-)Zellen — sonst bestimmten sie die Spaltenbreite.
const Unter: FC<{ children: ReactNode }> = ({ children }) => (
  <Box component="span" sx={{ display: 'block', fontSize: 11, color: 'text.secondary', whiteSpace: 'normal' }}>
    {children}
  </Box>
);

const W1_STANDARDS: Record<Breite, readonly string[]> = {
  breit: ['p', 'wert', 'cpa', 'anteil', 'wT', 'wS'],
  mittel: ['p', 'wert', 'cpa', 'anteil', 'wT', 'wS'],
  schmal: ['p', 'wert', 'anteil'],
};
const W2_STANDARDS: Record<Breite, readonly string[]> = {
  breit: ['a', 'wert', 'erst', 'n'],
  mittel: ['a', 'wert', 'erst', 'n'],
  schmal: ['a', 'wert', 'erst'],
};

const anteilGrund = (r: AnfoWertProdukt): string | null => {
  if (r.kind === 'einmal') return 'Einmalkauf, keine Zahlungsart.';
  if (r.share_sofort === null) return 'Keine Anfos mit Zahlungsart in 365 Tagen; das Modell nimmt den Schnitt aller Produkte.';
  return null;
};

const zelleWahl = (k: 'T' | 'S', feld: 'n' | 'r' | 'start'): Spalte<AnfoWertProdukt> => {
  const label = feld === 'n' ? TERMS.anfosInMessung.label : feld === 'r' ? TERMS.bisTag75.label : TERMS.startwert.label;
  const tip = feld === 'n' ? TERMS.anfosInMessung.tip : feld === 'r' ? TERMS.bisTag75.tip : TERMS.startwert.tip;
  const wert = (z: AnfoWertZelle | null, f: number): ReactNode =>
    z === null ? '–' : feld === 'n' ? int(z.n) : feld === 'r' ? <NettoEur brutto={z.r_brutto} f={f} /> : <NettoEur brutto={z.start_brutto} f={f} zusatz={z.start_source ? START_QUELLE[z.start_source] ?? z.start_source : undefined} />;
  return {
    key: `${feld}${k}`,
    label: `${label} · ${ZAHLUNGSART[k]}`,
    tip,
    gruppe: 'Modell',
    align: 'right',
    render: (r) => wert(r[k], r.netto_faktor),
    sortValue: (r) => (r[k] ? (feld === 'n' ? r[k]?.n ?? null : feld === 'r' ? r[k]?.r_brutto ?? null : r[k]?.start_brutto ?? null) : null),
  };
};

/** W2-Zeile: Gruppe (Produkt × Zahlungsart) oder Angebot. */
interface W2Zeile {
  key: string;
  gruppe: boolean;
  plx_product: string;
  zahlungsart: 'T' | 'S';
  netto_faktor: number;
  angebot?: AnfoWertAngebot;
  anzahl?: number;
  wertMin?: number;
  wertMax?: number;
  n?: number | null;
  kinder?: W2Zeile[];
}

const AnfoWertModell: FC = () => {
  const { data, fehler } = useLaden(fetchAnfoWertModell);
  const fallback = data?.fallback ?? false;

  // ── W1 ──────────────────────────────────────────────────────────────────────────────────
  const w1 = (): Spalte<AnfoWertProdukt>[] => [
    { key: 'p', label: 'Produkt', gruppe: 'Menge', pflicht: true, render: (r) => <ProduktZelle code={r.plx_product} name={r.name} />, sortValue: (r) => r.plx_product, minWidth: 140 },
    {
      key: 'wert',
      // B45 (26.10.2026): der Modellwert je Produkt heißt sektionsweit „Einzelwert des Produkts“ (wie in P1); „Erwarteter
      // Wert je Anfo“ bleibt dem Wert je Angebot und dem Ø über bestellte Anfos vorbehalten.
      label: TERMS.einzelwertNetto.label,
      tip: `${TERMS.einzelwertNetto.tip} Weicht vom „Ø je Anfo“ in Webinare & Produkte ab: Dort mittelt der Wert über die tatsächlich bestellten Angebote.`,
      einheit: 'je Produkt, netto',
      gruppe: 'Wert',
      pflicht: true,
      align: 'right',
      render: (r) => (
        <Box sx={{ lineHeight: 1.25 }}>
          <NettoEur
            brutto={r.value_brutto}
            f={r.netto_faktor}
            dick
            zusatz={
              r.kind === 'einmal' && r.onetime_price !== null && r.onetime_p !== null
                ? `Einmalkauf: Preis ${formatEur(r.onetime_price)} × ${formatPct(r.onetime_p * 100, 0)} bezahlt × 0,945`
                : r.rechnerisch_brutto !== null && r.value_brutto !== null && Math.abs(r.rechnerisch_brutto - r.value_brutto) > 0.01
                  ? `rechnerisch ${formatEur(r.rechnerisch_brutto)} vor dem Mitteln`
                  : undefined
            }
          />
          {r.kind === 'neu' && <Unter>nach dem Lauf angelegt</Unter>}
        </Box>
      ),
      sortValue: (r) => r.value_netto,
    },
    { key: 'cpa', label: TERMS.einkaufspreisJeAnfo.label, tip: TERMS.einkaufspreisJeAnfo.tip, einheit: 'netto', gruppe: 'Wert', align: 'right', render: (r) => formatEur(r.cpa_netto), sortValue: (r) => r.cpa_netto },
    ...(fallback
      ? []
      : ([
          {
            key: 'anteil',
            label: TERMS.anteilSofortzahlung.label,
            tip: TERMS.anteilSofortzahlung.tip,
            gruppe: 'Modell',
            align: 'right',
            render: (r) => {
              const grund = anteilGrund(r);
              if (grund)
                return (
                  <Tooltip title={grund} arrow placement="top">
                    <Box component="span" sx={{ cursor: 'help' }}>
                      —
                    </Box>
                  </Tooltip>
                );
              return (
                <Box sx={{ lineHeight: 1.25 }}>
                  {formatPct((r.share_sofort ?? 0) * 100, 0)}
                  <Unter>
                    {/* B05/B36 (26.10.2026): oben der geglättete Modellwert, hier der GEMESSENE Anteil — sichtbar getrennt. */}
                    {r.has_sofort_offer === false
                      ? 'kein Angebot mit Sofortzahlung'
                      : r.has_trial_offer === false
                        ? 'kein Angebot mit Testphase'
                        : (r.n365 ?? 0) === 0
                          ? 'keine Anfos in 365 Tagen: Schnitt aller Produkte'
                          : `gemessen: ${int(r.s365)} von ${int(r.n365)} (${formatPct(((r.s365 ?? 0) / (r.n365 ?? 1)) * 100, 0)})`}
                  </Unter>
                </Box>
              );
            },
            sortValue: (r) => r.share_sofort,
          },
          { key: 'wT', label: TERMS.wertTestphase.label, tip: TERMS.wertTestphase.tip, einheit: 'netto', gruppe: 'Wert', align: 'right', render: (r) => <NettoEur brutto={r.T?.value_brutto} f={r.netto_faktor} zusatz={r.T ? `× Faktor ${faktor(r.T.g)}` : undefined} />, sortValue: (r) => r.T?.value_brutto ?? null },
          { key: 'wS', label: TERMS.wertSofortzahlung.label, tip: TERMS.wertSofortzahlung.tip, einheit: 'netto', gruppe: 'Wert', align: 'right', render: (r) => <NettoEur brutto={r.S?.value_brutto} f={r.netto_faktor} zusatz={r.S ? `× Faktor ${faktor(r.S.g)}` : undefined} />, sortValue: (r) => r.S?.value_brutto ?? null },
          zelleWahl('T', 'n'),
          zelleWahl('S', 'n'),
          zelleWahl('T', 'r'),
          zelleWahl('S', 'r'),
          zelleWahl('T', 'start'),
          zelleWahl('S', 'start'),
        ] as Spalte<AnfoWertProdukt>[])),
  ];
  const w1Meta = nachGruppenReihenfolge(spaltenMeta(w1()), ['Menge', 'Wert', 'Modell']);
  const w1Wahl = useSpaltenwahl('anfoWertProdukt', w1Meta, W1_STANDARDS);
  const w1Sichtbar = new Set(w1Wahl.sichtbar);
  // Unter „breit" ohne Mindestbreite der Namensspalte (Abnahme 25.10.2026: 366/267 und 767/654 px).
  const w1Spalten = w1()
    .filter((c) => w1Sichtbar.has(c.key))
    .map((c) => (c.key === 'p' && w1Wahl.breite !== 'breit' ? { ...c, minWidth: undefined } : c));

  // ── W3 ──────────────────────────────────────────────────────────────────────────────────
  const summe = data?.bestellungen_summe;
  // W3 ohne Spaltenwahl (P2-V3 § 5): schmal (< 640 px Karte) stehen Anzahl, „nie bezahlt" und Ø als Unterzeilen — fünf
  // Spalten liefen bei 267 und 414 px waagrecht über (Abnahme 25.10.2026).
  const [w3Ref, w3Px] = useBreitePx();
  const w3Schmal = w3Px !== null && breiteAus(w3Px) === 'schmal';
  const nie = (n: number, nNull: number) => `${int(nNull)} · ${formatPct(n > 0 ? (nNull / n) * 100 : null, 0)}`;
  const w3Name = (code: string, name: string | null, n: number, nNull: number) => (
    <Box component="span" sx={{ lineHeight: 1.3 }}>
      {code ? <ProduktZelle code={code} name={name} /> : 'Alle Produkte'}
      {w3Schmal && (
        <Unter>
          {int(n)} mit Hochrechnung · nie bezahlt {nie(n, nNull)}
        </Unter>
      )}
    </Box>
  );
  const w3Spalten: Spalte<AnfoWertBestellungen>[] = [
    {
      key: 'p',
      label: 'Produkt',
      render: (r) => w3Name(r.plx_product, r.name, r.n, r.n_null),
      sortValue: (r) => r.plx_product,
      minWidth: w3Schmal ? undefined : 140,
      summary: summe && w3Schmal ? w3Name('', null, summe.n, summe.n_null) : undefined,
    },
    ...((w3Schmal
      ? []
      : [
          { key: 'n', label: TERMS.anfosMitHochrechnung.label, tip: TERMS.anfosMitHochrechnung.tip, align: 'right', render: (r) => int(r.n), sortValue: (r) => r.n, summary: summe ? int(summe.n) : undefined },
          {
            key: 'null',
            label: TERMS.nieBezahltWert0.label,
            tip: TERMS.nieBezahltWert0.tip,
            align: 'right',
            render: (r) => nie(r.n, r.n_null),
            sortValue: (r) => r.n_null,
            summary: summe ? nie(summe.n, summe.n_null) : undefined,
          },
          { key: 'avg', label: `Ø ${TERMS.hochrechnung.label}`, tip: TERMS.hochrechnung.tip, einheit: 'netto', align: 'right', render: (r) => formatEur(r.avg_netto), sortValue: (r) => r.avg_netto, summary: summe ? formatEur(summe.avg_netto) : undefined },
        ]) as Spalte<AnfoWertBestellungen>[]),
    {
      key: 'sum',
      label: 'Summe',
      einheit: 'netto',
      align: 'right',
      render: (r) => (
        <Box component="span" sx={{ lineHeight: 1.25 }}>
          {tEuro(r.sum_netto)}
          {w3Schmal && <Unter>Ø {formatEur(r.avg_netto, 0)}</Unter>}
        </Box>
      ),
      sortValue: (r) => r.sum_netto,
      summary: summe ? (
        <Box component="span" sx={{ lineHeight: 1.25 }}>
          {tEuro(summe.sum_netto)}
          {w3Schmal && <Unter>Ø {formatEur(summe.avg_netto, 0)}</Unter>}
        </Box>
      ) : undefined,
    },
  ];

  // ── W2 ──────────────────────────────────────────────────────────────────────────────────
  const gruppen: W2Zeile[] = useMemo(() => {
    const out = new Map<string, W2Zeile>();
    const produktZelle = new Map((data?.produkte ?? []).map((p) => [p.plx_product, p]));
    for (const a of data?.angebote ?? []) {
      const key = `${a.plx_product}|${a.zahlungsart}`;
      const g =
        out.get(key) ??
        ({
          key,
          gruppe: true,
          plx_product: a.plx_product,
          zahlungsart: a.zahlungsart,
          netto_faktor: a.netto_faktor,
          anzahl: 0,
          wertMin: Number.POSITIVE_INFINITY,
          wertMax: Number.NEGATIVE_INFINITY,
          n: produktZelle.get(a.plx_product)?.[a.zahlungsart]?.n ?? a.n,
          kinder: [],
        } as W2Zeile);
      g.anzahl = (g.anzahl ?? 0) + 1;
      g.wertMin = Math.min(g.wertMin ?? Infinity, a.value_netto);
      g.wertMax = Math.max(g.wertMax ?? -Infinity, a.value_netto);
      g.kinder?.push({ key: a.werbeaktion_wbkz, gruppe: false, plx_product: a.plx_product, zahlungsart: a.zahlungsart, netto_faktor: a.netto_faktor, angebot: a });
      out.set(key, g);
    }
    return [...out.values()];
  }, [data]);
  const erstNetto = (a: AnfoWertAngebot) => netto(a.first_amount, a.netto_faktor);
  const w2 = (): Spalte<W2Zeile>[] => [
    {
      key: 'a',
      label: TERMS.aktionscode.label,
      tip: TERMS.aktionscode.tip,
      gruppe: 'Menge',
      pflicht: true,
      minWidth: 150,
      render: (r) =>
        r.gruppe ? (
          <Box component="span" sx={{ fontWeight: 600 }}>
            {r.plx_product} · {ZAHLUNGSART[r.zahlungsart]} · {int(r.anzahl)} {r.anzahl === 1 ? 'Angebot' : 'Angebote'}
          </Box>
        ) : (
          <Box component="span">
            {r.angebot?.werbeaktion_wbkz}
            {r.angebot?.wert_geschaetzt && (
              <Tooltip title={TERMS.wertGeschaetzt.tip} arrow placement="top">
                <Box component="span" sx={{ cursor: 'help' }}>
                  <Etikett>{TERMS.wertGeschaetzt.short}</Etikett>
                </Box>
              </Tooltip>
            )}
            {r.angebot?.neu_bewertet && <Unter>seit dem Lauf neu bewertet</Unter>}
          </Box>
        ),
      sortValue: (r) => (r.gruppe ? r.key : r.angebot?.werbeaktion_wbkz ?? null),
    },
    {
      key: 'wert',
      label: TERMS.wertJeAnfoModell.label,
      tip: TERMS.wertJeAnfoModell.tip,
      einheit: 'je Angebot, netto',
      gruppe: 'Wert',
      pflicht: true,
      align: 'right',
      sortValue: (r) => (r.gruppe ? r.wertMax ?? null : r.angebot?.value_netto ?? null),
      render: (r) => {
        if (r.gruppe) {
          const gleich = Math.abs((r.wertMax ?? 0) - (r.wertMin ?? 0)) < 0.005;
          // Zwei Zeilen statt „131,80 € – 541,49 €" in einer: die Zelle ist rechtsbündig und bricht sonst nicht um.
          return gleich ? (
            <Box component="span" sx={{ lineHeight: 1.25 }}>
              {formatEur(r.wertMin ?? null)}
              {(r.anzahl ?? 0) > 1 && <Unter>alle gleich</Unter>}
            </Box>
          ) : (
            <Box component="span" sx={{ lineHeight: 1.25 }}>
              {formatEur(r.wertMin ?? null)}
              <Unter>bis {formatEur(r.wertMax ?? null)}</Unter>
            </Box>
          );
        }
        const a = r.angebot as AnfoWertAngebot;
        const e = erstNetto(a);
        return (
          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
            <NettoEur brutto={a.value_brutto} f={a.netto_faktor} zusatz={a.neu_bewertet && a.run_value_brutto !== null ? `vorher ${formatEur(a.run_value_brutto)}` : undefined} />
            {e !== null && a.value_netto < e && <InfoTip text={UNTER_ERSTBETRAG} />}
          </Box>
        );
      },
    },
    {
      key: 'erst',
      label: TERMS.erstbetrag.label,
      tip: TERMS.erstbetrag.tip,
      einheit: 'netto',
      gruppe: 'Wert',
      align: 'right',
      sortValue: (r) => (r.angebot ? r.angebot.first_amount : null),
      render: (r) => (r.angebot ? <NettoEur brutto={r.angebot.first_amount} f={r.angebot.netto_faktor} /> : null),
    },
    {
      key: 'n',
      label: `${TERMS.anfosInMessung.label} (Gruppe)`,
      tip: TERMS.anfosInMessung.tip,
      gruppe: 'Menge',
      align: 'right',
      sortValue: (r) => (r.gruppe ? r.n ?? null : null),
      render: (r) => (r.gruppe ? int(r.n ?? null) : null),
    },
    { key: 'start', label: TERMS.startwert.label, tip: TERMS.startwert.tip, einheit: 'netto', gruppe: 'Modell', align: 'right', render: (r) => (r.angebot ? <NettoEur brutto={r.angebot.start_brutto} f={r.angebot.netto_faktor} /> : null) },
    { key: 'r', label: TERMS.bisTag75.label, tip: TERMS.bisTag75.tip, einheit: 'netto', gruppe: 'Modell', align: 'right', render: (r) => (r.angebot ? <NettoEur brutto={r.angebot.r_brutto} f={r.angebot.netto_faktor} zusatz={r.angebot.g !== null ? `× Faktor ${faktor(r.angebot.g)}` : undefined} /> : null) },
    {
      key: 'quote',
      label: TERMS.wertJeErstbetrag.label,
      tip: TERMS.wertJeErstbetrag.tip,
      gruppe: 'Modell',
      align: 'right',
      render: (r) => {
        const e = r.angebot ? erstNetto(r.angebot) : null;
        return r.angebot && e ? formatNumber(r.angebot.value_netto / e, 2) : null;
      },
    },
  ];
  const w2Meta = nachGruppenReihenfolge(spaltenMeta(w2()), ['Menge', 'Wert', 'Modell']);
  const w2Wahl = useSpaltenwahl('anfoWertAngebot', w2Meta, W2_STANDARDS);
  const w2Sichtbar = new Set(w2Wahl.sichtbar);
  const w2Spalten = w2()
    .filter((c) => w2Sichtbar.has(c.key))
    .map((c) => (c.key === 'a' && w2Wahl.breite !== 'breit' ? { ...c, minWidth: undefined } : c));

  const k = data?.kopf;
  const kopfTip = k
    ? `Faktor auf 36 Monate: Testphase ${faktor(k.g_t)}, Sofortzahlung ${faktor(k.g_s)}; nach gezahltem Typ: Quartal ${faktor(k.g_q)}, Jahr ${faktor(k.g_u)}. Der Startwert wiegt so viel wie ${int(k.k)} gezählte Anfos. Einkaufspreis-Anteil: ${data?.pct === null || data?.pct === undefined ? '–' : `${int(data.pct)} %`}.`
    : '';
  return (
    <ChapterPage
      title="Wie der Anfo-Wert entsteht"
      question="Für Controlling: wie der Erwartete Wert je Bestellung gerechnet wird — je Produkt, je Angebot und je Bestellung."
      zurueck={{ to: '/auswertungen/umsatz', label: 'Umsatz & Bezahlqualität' }}
      standQuellen={[{ key: 'anfo_werte', label: 'Anfo-Werte' }, { key: 'plx_orders', label: 'Bestellsystem (PL-X)' }]}
    >
      <Explainer title="Wie das Modell rechnet" sections={EXPLAINER} />
      {!data ? (
        <Laden fehler={fehler} />
      ) : (
        <>
          {data.fallback && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Modell m4 aktiv (Rückweg): Jede Anfo trägt den Wert ihres Produkts. Werte je Angebot und je Bestellung gibt es in diesem Zustand nicht.
            </Alert>
          )}
          <Box sx={{ display: 'flex', alignItems: 'center', fontSize: 14, mb: 2 }}>
            Modellstand vom <strong style={{ margin: '0 4px' }}>{data.run ? formatBerlinWallclock(data.run.as_of) : '–'}</strong>
            {data.run ? `(${LAUF[data.run.mode] ?? data.run.mode}, Modell ${k?.model_version ?? data.run.model_version})` : ''}
            {kopfTip && <InfoTip text={kopfTip} />}
          </Box>
          {/* Paket M (Workshop-Lücken 29.10.2026): eigene Endpunkte, laden unabhängig von der Seite. */}
          {!data.fallback && <BindungKarte />}
          <ChartCard
            title="Einzelwert je Produkt"
            actions={<SpaltenMenue spalten={w1Meta} wahl={w1Wahl} />}
            howToRead="Je Produkt der Einzelwert, netto, und woraus er entsteht: der Wert bei Testphase und bei Sofortzahlung, gemischt nach dem Anteil Sofortzahlung im Modell. Daneben der vertretbare Einkaufspreis je Anfo. Eine einzelne Anfo trägt den Wert ihres Angebots (Karte unten); der Ø je Anfo in Webinare & Produkte mittelt über die bestellten Angebote und weicht deshalb ab. Wie das Modell zählt (gezählte Anfos, Zahlung bis Tag 75, Startwert), steht im Spaltenmenü."
            terms={[
              { label: TERMS.einzelwertNetto.label, tip: TERMS.einzelwertNetto.tip },
              { label: TERMS.einkaufspreisJeAnfo.label, tip: TERMS.einkaufspreisJeAnfo.tip },
              { label: TERMS.anteilSofortzahlung.label, tip: TERMS.anteilSofortzahlung.tip },
            ]}
            source="Lauf des Anfo-Werts (Parameter und Historie), aktuelle Produktwerte, PL-X-Zahlungen"
            freshnessKey={['anfo_werte', 'plx_orders']}
          >
            <Box ref={w1Wahl.messRef}>
              <SortableTable rows={data.produkte} columns={w1Spalten} rowKey={(r) => r.plx_product} maxHeight={null} eng={w1Wahl.breite !== 'breit'} />
              <Hinweis>
                Alle Beträge netto; die Bruttowerte stehen im ⓘ der Zelle.
                {data.last_run && data.last_run.id !== data.run?.id && data.last_run.mode !== 'm4-snapshot'
                  ? ` Zuletzt geändert am ${formatBerlinWallclock(data.last_run.as_of)} (${LAUF[data.last_run.mode] ?? data.last_run.mode}).`
                  : ''}
              </Hinweis>
            </Box>
          </ChartCard>
          {!data.fallback && (
            <>
              <ChartCard
                title="Bestellungen mit Hochrechnung je Produkt"
                howToRead="Anfos, deren Zahlung alt genug ist und deren eigener Wert deshalb hochgerechnet ist — wie viele es je Produkt sind, wie viele davon nie bezahlt haben (Wert 0 €) und was sie zusammen erwarten lassen."
                terms={[
                  { label: TERMS.anfosMitHochrechnung.label, tip: TERMS.anfosMitHochrechnung.tip },
                  { label: TERMS.nieBezahltWert0.label, tip: TERMS.nieBezahltWert0.tip },
                  { label: TERMS.hochrechnung.label, tip: TERMS.hochrechnung.tip },
                ]}
                source="Wert je Bestellung (tägliche Auffrischung des Anfo-Werts), PL-X-Zahlungen"
                freshnessKey={['anfo_werte', 'plx_orders']}
              >
                <Box ref={w3Ref}>
                  <SortableTable rows={data.bestellungen} columns={w3Spalten} rowKey={(r) => r.plx_product} summaryLabel="Alle Produkte" maxHeight={null} eng={w3Schmal} />
                </Box>
                <Hinweis>Alle bewerteten Bestellungen ohne Storno und Testbestellungen. Jüngere Anfos fehlen hier — sie tragen noch den Wert ihres Angebots.</Hinweis>
              </ChartCard>
              <ChartCard
                title="Erwarteter Wert je Angebot"
                actions={<SpaltenMenue spalten={w2Meta} wahl={w2Wahl} />}
                howToRead="Je Produkt und Zahlungsart eine Gruppe, darunter ihre Angebote (Aktionscodes). Alle Testphasen-Angebote eines Produkts haben denselben Wert; bei Sofortzahlung hängt der Startwert am Erstbetrag des Angebots. „Anfos in der Messung“ gilt für die ganze Gruppe."
                terms={[
                  { label: TERMS.wertJeAnfoModell.label, tip: TERMS.wertJeAnfoModell.tip },
                  { label: TERMS.erstbetrag.label, tip: TERMS.erstbetrag.tip },
                  { label: TERMS.anfosInMessung.label, tip: TERMS.anfosInMessung.tip },
                  { label: TERMS.wertGeschaetzt.label, tip: TERMS.wertGeschaetzt.tip },
                ]}
                source="Wert je Angebot heute (Anfo-Wert-Lauf und tägliche Angebotsbewertung)"
                freshnessKey="anfo_werte"
              >
                <Box ref={w2Wahl.messRef}>
                  <Box sx={{ fontSize: 14, mb: 1.5, maxWidth: '75ch', lineHeight: 1.6 }}>{TERMS.wertBeiBestellung.tip}</Box>
                  <SortableTable
                    rows={gruppen}
                    columns={w2Spalten}
                    rowKey={(r) => r.key}
                    kinder={(r) => r.kinder}
                    initialOffen={gruppen.map((g) => g.key)}
                    kinderText={(r, offen) => (offen ? 'ausblenden' : `${int(r.anzahl)} Angebote`)}
                    eng={w2Wahl.breite === 'schmal'}
                    maxHeight={null}
                    // Rund 150 Zeilen offen: Kopf klebt am Seiten-Scroll (P2-V3 § 5, nur breit — ohne eigenen Scroll-Container).
                    kopfKlebtSeite={w2Wahl.breite === 'breit' ? true : undefined}
                  />
                  <Hinweis>
                    {int(data.angebote.length)} Angebote in {int(gruppen.length)} Gruppen, davon {int(data.angebote.filter((a) => a.neu_bewertet).length)} seit dem Lauf neu bewertet
                    {data.angebote.some((a) => a.wert_geschaetzt) ? ` und ${int(data.angebote.filter((a) => a.wert_geschaetzt).length)} geschätzt` : ''}. Beträge netto, brutto im ⓘ.
                  </Hinweis>
                </Box>
              </ChartCard>
              <PruefstandKarte />
            </>
          )}
        </>
      )}
    </ChapterPage>
  );
};

export default AnfoWertModell;
